import React, { useState } from "react";
import Container from "react-bootstrap/Container";
import Card from "react-bootstrap/Card";
import Button from "react-bootstrap/Button";
import Form from "react-bootstrap/Form";
import Alert from "react-bootstrap/Alert";
import Row from "react-bootstrap/Row";
import Col from "react-bootstrap/Col";
import Spinner from "react-bootstrap/Spinner";
import Badge from "react-bootstrap/Badge";
import ProgressBar from "react-bootstrap/ProgressBar";
import Modal from "react-bootstrap/Modal";
import Accordion from "react-bootstrap/Accordion";
import Table from "react-bootstrap/Table";
import Tab from "react-bootstrap/Tab";
import Tabs from "react-bootstrap/Tabs";
import {
  Upload,
  FileText,
  CheckCircle,
  AlertCircle,
  BookOpen,
  Save,
  Eye,
  FileUp,
  Layers,
  List,
  Edit3,
  RefreshCw
} from "lucide-react";

// Use local backend for admin import - change to production URL for deployment
const API_BASE = process.env.REACT_APP_API_BASE || "http://localhost:4000/api";
const brandGreen = "#11998e";

export default function AdminDocxImport() {
  const [file, setFile] = useState(null);
  const [yearLevel, setYearLevel] = useState("2");
  const [semester, setSemester] = useState("3");
  const [language, setLanguage] = useState("de");

  const [importing, setImporting] = useState(false);
  const [progress, setProgress] = useState(0);
  const [progressText, setProgressText] = useState("");
  const [error, setError] = useState(null);
  const [importedContent, setImportedContent] = useState(null);
  const [showPreview, setShowPreview] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [activeModuleTab, setActiveModuleTab] = useState(null);
  const [expandedLesson, setExpandedLesson] = useState(null);
  const [importHistory, setImportHistory] = useState([]);

  const getSemesterOptions = (year) => {
    const yearNum = parseInt(year);
    const firstSem = (yearNum - 1) * 2 + 1;
    const secondSem = firstSem + 1;
    return [
      { value: String(firstSem), label: `${firstSem}. Semester` },
      { value: String(secondSem), label: `${secondSem}. Semester` }
    ];
  };

  const handleYearChange = (newYear) => {
    setYearLevel(newYear);
    const firstSem = (parseInt(newYear) - 1) * 2 + 1;
    setSemester(String(firstSem));
  };

  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile) {
      const isDocx = selectedFile.name.endsWith(".docx") ||
        selectedFile.type === "application/vnd.openxmlformats-officedocument.wordprocessingml.document";

      if (isDocx) {
        setFile(selectedFile);
        setError(null);
      } else {
        setError("Nur DOCX-Dateien sind erlaubt");
      }
    }
  };

  const handleImport = async () => {
    if (!file) {
      setError("Bitte wählen Sie zuerst eine DOCX-Datei aus");
      return;
    }

    setImporting(true);
    setError(null);
    setProgress(10);
    setProgressText("Datei wird hochgeladen...");

    try {
      const formData = new FormData();
      formData.append("docx", file);
      formData.append("yearLevel", yearLevel);
      formData.append("semester", semester);
      formData.append("autoSave", "false");

      setProgress(40);
      setProgressText("Dokument wird analysiert...");

      const res = await fetch(`${API_BASE}/admin/import-docx?lang=${language}`, {
        method: "POST",
        body: formData,
      });

      if (!res.ok) {
        const errData = await res.json().catch(() => ({}));
        throw new Error(errData.error || errData.details || `HTTP ${res.status}`);
      }

      const result = await res.json();

      setProgress(90);
      setProgressText("Struktur wird erstellt...");

      if (result.ok && result.content) {
        setImportedContent(result.content);
        // Set first module as active tab
        if (result.content.modules && result.content.modules.length > 0) {
          setActiveModuleTab(result.content.modules[0].id);
        }
        setProgress(100);
        setProgressText("Import abgeschlossen!");
        setShowPreview(true);
      } else {
        throw new Error("Invalid response");
      }
    } catch (err) {
      console.error("Import error:", err);
      let errorMsg = `Import fehlgeschlagen: ${err.message}`;
      if (err.message === "docx_parser_not_available") {
        errorMsg = "DOCX-Parser nicht verfügbar. Bitte mammoth installieren: npm install mammoth";
      }
      setError(errorMsg);
    } finally {
      setImporting(false);
    }
  };

  const handleSaveContent = async () => {
    if (!importedContent) return;

    setSaving(true);
    setError(null);

    try {
      const res = await fetch(`${API_BASE}/admin/save-imported-content?lang=${language}`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json"
        },
        body: JSON.stringify({ content: importedContent }),
      });

      if (!res.ok) {
        const errData = await res.json().catch(() => ({}));
        throw new Error(errData.details || "Save failed");
      }

      const result = await res.json();

      setSaveSuccess(true);

      // Add all modules to history
      const newHistory = importedContent.modules.map(mod => ({
        id: mod.id,
        title: mod.title,
        lessons: mod.lessons.length,
        timestamp: new Date().toLocaleString()
      }));
      setImportHistory(prev => [...newHistory, ...prev].slice(0, 20));

      setTimeout(() => {
        setShowPreview(false);
        setImportedContent(null);
        setFile(null);
        setSaveSuccess(false);
        setProgress(0);
      }, 2000);
    } catch (err) {
      console.error("Save error:", err);
      setError(`Speichern fehlgeschlagen: ${err.message}`);
    } finally {
      setSaving(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    const droppedFile = e.dataTransfer.files[0];
    if (droppedFile && droppedFile.name.endsWith(".docx")) {
      setFile(droppedFile);
      setError(null);
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
  };

  // Count totals for preview
  const getTotals = () => {
    if (!importedContent?.modules) return { modules: 0, lessons: 0, exercises: 0 };
    let lessons = 0;
    let exercises = 0;
    for (const mod of importedContent.modules) {
      lessons += mod.lessons?.length || 0;
      for (const lesson of mod.lessons || []) {
        exercises += lesson.exercises?.length || 0;
      }
    }
    return { modules: importedContent.modules.length, lessons, exercises };
  };

  return (
    <div
      style={{
        minHeight: "100vh",
        width: "100%",
        background: "linear-gradient(to bottom right, #e0fff5, white)",
        padding: "2rem 0",
      }}
    >
      <Container style={{ maxWidth: 1000 }}>
        {/* Header */}
        <Card className="shadow border-0 mb-4">
          <Card.Body className="p-4">
            <div className="d-flex align-items-center gap-3">
              <div
                className="rounded-circle d-flex justify-content-center align-items-center"
                style={{ width: 48, height: 48, background: brandGreen }}
              >
                <FileUp className="text-white" size={24} />
              </div>
              <div>
                <h4 className="mb-1">Word-Dokument zu Modulen</h4>
                <p className="mb-0 text-muted">
                  DOCX-Dateien mit mehreren Modulen hochladen und konvertieren
                </p>
              </div>
            </div>
          </Card.Body>
        </Card>

        {error && (
          <Alert variant="danger" dismissible onClose={() => setError(null)} className="mb-4">
            <AlertCircle size={16} className="me-2" />
            {error}
          </Alert>
        )}

        {/* Import Card */}
        <Card className="shadow border-0 mb-4">
          <Card.Header style={{ background: brandGreen }} className="text-white py-3">
            <h5 className="mb-0">
              <Upload size={20} className="me-2" />
              Neuer Import
            </h5>
          </Card.Header>
          <Card.Body className="p-4">
            {/* Options Row */}
            <Row className="mb-3">
              <Col md={4}>
                <Form.Group>
                  <Form.Label>Sprache</Form.Label>
                  <Form.Select value={language} onChange={(e) => setLanguage(e.target.value)}>
                    <option value="de">Deutsch</option>
                    <option value="en">English</option>
                  </Form.Select>
                </Form.Group>
              </Col>
              <Col md={4}>
                <Form.Group>
                  <Form.Label>Standard-Jahrgang</Form.Label>
                  <Form.Select value={yearLevel} onChange={(e) => handleYearChange(e.target.value)}>
                    <option value="1">1. Klasse</option>
                    <option value="2">2. Klasse</option>
                    <option value="3">3. Klasse</option>
                    <option value="4">4. Klasse</option>
                    <option value="5">5. Klasse</option>
                  </Form.Select>
                </Form.Group>
              </Col>
              <Col md={4}>
                <Form.Group>
                  <Form.Label>Standard-Semester</Form.Label>
                  <Form.Select value={semester} onChange={(e) => setSemester(e.target.value)}>
                    {getSemesterOptions(yearLevel).map(opt => (
                      <option key={opt.value} value={opt.value}>{opt.label}</option>
                    ))}
                  </Form.Select>
                </Form.Group>
              </Col>
            </Row>

            {/* File Upload */}
            <Form.Group className="mb-3">
              <Form.Label><strong>DOCX-Datei</strong></Form.Label>
              <div
                className={`border rounded p-4 text-center ${file ? "border-success bg-success bg-opacity-10" : ""}`}
                style={{ borderStyle: file ? "solid" : "dashed", cursor: "pointer" }}
                onClick={() => document.getElementById("docx-upload").click()}
                onDrop={handleDrop}
                onDragOver={handleDragOver}
              >
                <input
                  type="file"
                  id="docx-upload"
                  accept=".docx"
                  onChange={handleFileChange}
                  style={{ display: "none" }}
                />
                {file ? (
                  <div className="d-flex align-items-center justify-content-center">
                    <FileText size={24} className="text-success me-2" />
                    <span className="fw-medium">{file.name}</span>
                    <Badge bg="success" className="ms-2">
                      {(file.size / 1024 / 1024).toFixed(2)} MB
                    </Badge>
                  </div>
                ) : (
                  <>
                    <Upload size={32} className="text-muted mb-2" />
                    <p className="mb-0 text-muted">Klicken oder DOCX-Datei hierher ziehen</p>
                    <small className="text-muted">Max. 20 MB</small>
                  </>
                )}
              </div>
            </Form.Group>

            {/* Format Hint */}
            <Alert variant="info" className="small mb-3">
              <strong>Erwartetes Format:</strong>
              <ul className="mb-0 mt-1">
                <li><strong>Klasse/Semester:</strong> "2. Klasse – 4. Semester"</li>
                <li><strong>Module:</strong> "Modul 1 – PV" oder "MODUL X"</li>
                <li><strong>Lektionen:</strong> "LEKTION X" gefolgt vom Titel</li>
                <li><strong>Inhalte:</strong> "Inhaltsabschnitt X" mit Text</li>
                <li><strong>Quiz:</strong> "QUIZ – LEKTION X" mit "Aufgabe X"</li>
                <li><strong>Antworten:</strong> Richtig mit ✅ markiert</li>
              </ul>
            </Alert>

            {/* Progress */}
            {importing && (
              <div className="mb-3">
                <div className="d-flex justify-content-between mb-1">
                  <span className="small">{progressText}</span>
                  <span className="small">{progress}%</span>
                </div>
                <ProgressBar animated now={progress} variant="success" />
              </div>
            )}

            {/* Import Button */}
            <Button
              size="lg"
              className="w-100"
              style={{ background: brandGreen, borderColor: brandGreen }}
              onClick={handleImport}
              disabled={importing || !file}
            >
              {importing ? (
                <>
                  <Spinner animation="border" size="sm" className="me-2" />
                  Importiere...
                </>
              ) : (
                <>
                  <FileUp size={18} className="me-2" />
                  Dokument importieren
                </>
              )}
            </Button>
          </Card.Body>
        </Card>

        {/* Import History */}
        {importHistory.length > 0 && (
          <Card className="shadow border-0">
            <Card.Header className="bg-light">
              <h6 className="mb-0">
                <RefreshCw size={16} className="me-2" />
                Letzte Importe
              </h6>
            </Card.Header>
            <Card.Body className="p-0">
              <Table striped hover className="mb-0">
                <thead>
                  <tr>
                    <th>Modul-ID</th>
                    <th>Titel</th>
                    <th>Lektionen</th>
                    <th>Zeitpunkt</th>
                  </tr>
                </thead>
                <tbody>
                  {importHistory.map((item, idx) => (
                    <tr key={idx}>
                      <td><code>{item.id}</code></td>
                      <td>{item.title}</td>
                      <td><Badge bg="info">{item.lessons}</Badge></td>
                      <td className="text-muted small">{item.timestamp}</td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </Card.Body>
          </Card>
        )}
      </Container>

      {/* Preview Modal */}
      <Modal show={showPreview} onHide={() => setShowPreview(false)} size="xl" fullscreen="lg-down" scrollable>
        <Modal.Header closeButton className="bg-light">
          <Modal.Title>
            <Eye size={20} className="me-2" />
            Importierte Inhalte
            {importedContent?.modules && (
              <Badge bg="success" className="ms-2">
                {getTotals().modules} Module, {getTotals().lessons} Lektionen, {getTotals().exercises} Übungen
              </Badge>
            )}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ maxHeight: "70vh" }}>
          {saveSuccess ? (
            <div className="text-center py-5">
              <CheckCircle size={64} className="text-success mb-3" />
              <h4 className="text-success">Erfolgreich gespeichert!</h4>
              <p className="text-muted">
                {getTotals().modules} Module mit {getTotals().lessons} Lektionen wurden importiert.
              </p>
            </div>
          ) : importedContent?.modules && importedContent.modules.length > 0 ? (
            <Tabs
              activeKey={activeModuleTab}
              onSelect={setActiveModuleTab}
              className="mb-3"
            >
              {importedContent.modules.map((module, modIdx) => (
                <Tab
                  key={module.id}
                  eventKey={module.id}
                  title={
                    <span>
                      <Badge bg="secondary" className="me-1">{modIdx + 1}</Badge>
                      {module.title.slice(0, 30)}{module.title.length > 30 ? '...' : ''}
                    </span>
                  }
                >
                  {/* Module Info */}
                  <Card className="mb-3 border-success">
                    <Card.Header className="bg-success text-white">
                      <h5 className="mb-0">
                        <Layers size={18} className="me-2" />
                        {module.title}
                      </h5>
                    </Card.Header>
                    <Card.Body>
                      <Row>
                        <Col md={6}>
                          <p className="mb-1"><strong>ID:</strong> <code>{module.id}</code></p>
                          <p className="mb-0"><strong>Beschreibung:</strong> {module.description || "-"}</p>
                        </Col>
                        <Col md={6}>
                          <p className="mb-1"><strong>Klasse:</strong> {module.year_level}</p>
                          <p className="mb-0"><strong>Semester:</strong> {module.semester}</p>
                        </Col>
                      </Row>
                    </Card.Body>
                  </Card>

                  {/* Lessons */}
                  <h5 className="mb-3">
                    <BookOpen size={18} className="me-2" />
                    Lektionen ({module.lessons?.length || 0})
                  </h5>

                  <Accordion activeKey={expandedLesson} onSelect={setExpandedLesson}>
                    {module.lessons?.map((lesson, idx) => (
                      <Accordion.Item key={lesson.id} eventKey={lesson.id}>
                        <Accordion.Header>
                          <div className="d-flex justify-content-between align-items-center w-100 me-3">
                            <span>
                              <Badge bg="secondary" className="me-2">{idx + 1}</Badge>
                              <strong>{lesson.title}</strong>
                            </span>
                            <div>
                              <Badge bg="info" className="me-1">
                                {lesson.content_sections?.length || 0} Abschnitte
                              </Badge>
                              <Badge bg="warning" text="dark">
                                {lesson.exercises?.length || 0} Übungen
                              </Badge>
                            </div>
                          </div>
                        </Accordion.Header>
                        <Accordion.Body>
                          <p className="text-muted mb-3">{lesson.description || "-"}</p>

                          {lesson.learning_objectives?.length > 0 && (
                            <div className="mb-3">
                              <strong className="small">Lernziele:</strong>
                              <ul className="small mb-0">
                                {lesson.learning_objectives.map((obj, i) => (
                                  <li key={i}>{obj}</li>
                                ))}
                              </ul>
                            </div>
                          )}

                          {lesson.content_sections?.length > 0 && (
                            <div className="mb-3">
                              <strong className="small d-block mb-2">
                                <List size={14} className="me-1" />
                                Inhaltssektionen:
                              </strong>
                              {lesson.content_sections.map((section) => (
                                <Card key={section.id} className="mb-2 border-light">
                                  <Card.Body className="py-2 px-3">
                                    <div className="d-flex justify-content-between">
                                      <strong className="small">{section.title || "(Ohne Titel)"}</strong>
                                      <Badge bg="light" text="muted" className="small">
                                        {section.markdown?.length || 0} Zeichen
                                      </Badge>
                                    </div>
                                    <p className="small text-muted mb-0" style={{ whiteSpace: "pre-wrap" }}>
                                      {section.markdown?.slice(0, 200)}{section.markdown?.length > 200 ? '...' : ''}
                                    </p>
                                  </Card.Body>
                                </Card>
                              ))}
                            </div>
                          )}

                          {lesson.exercises?.length > 0 && (
                            <div>
                              <strong className="small d-block mb-2">
                                <Edit3 size={14} className="me-1" />
                                Übungen:
                              </strong>
                              {lesson.exercises.map((exercise) => (
                                <Card key={exercise.id} className="mb-2 border-warning">
                                  <Card.Body className="py-2 px-3">
                                    <div className="d-flex justify-content-between align-items-start">
                                      <div>
                                        <Badge
                                          bg={
                                            exercise.type === "single_choice" ? "primary" :
                                            exercise.type === "input" ? "info" :
                                            "secondary"
                                          }
                                          className="me-2"
                                        >
                                          {exercise.type}
                                        </Badge>
                                        <strong className="small">{exercise.title}</strong>
                                        <code className="ms-2 small">{exercise.id}</code>
                                      </div>
                                      <Badge bg="light" text="dark">{exercise.points} Pkt</Badge>
                                    </div>
                                    <p className="small text-muted mb-1 mt-1" style={{ whiteSpace: "pre-wrap" }}>
                                      {exercise.prompt?.slice(0, 150)}{exercise.prompt?.length > 150 ? '...' : ''}
                                    </p>
                                    {exercise.type === "single_choice" && exercise.config?.choices && (
                                      <div className="small">
                                        {exercise.config.choices.map((c, i) => (
                                          <span key={i} className={`me-2 ${c.is_correct ? 'text-success fw-bold' : 'text-muted'}`}>
                                            {c.text.slice(0, 30)}{c.is_correct ? ' ✓' : ''}
                                          </span>
                                        ))}
                                      </div>
                                    )}
                                    {exercise.type === "input" && exercise.config?.answer && (
                                      <div className="small text-success">
                                        Antwort: <strong>{exercise.config.answer}</strong> {exercise.config.unit}
                                      </div>
                                    )}
                                  </Card.Body>
                                </Card>
                              ))}
                            </div>
                          )}
                        </Accordion.Body>
                      </Accordion.Item>
                    ))}
                  </Accordion>
                </Tab>
              ))}
            </Tabs>
          ) : (
            <Alert variant="warning">
              Keine Module im Dokument gefunden. Bitte prüfen Sie das Format.
            </Alert>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowPreview(false)} disabled={saving}>
            Abbrechen
          </Button>
          <Button
            style={{ background: brandGreen, borderColor: brandGreen }}
            onClick={handleSaveContent}
            disabled={saving || !importedContent?.modules?.length}
          >
            {saving ? (
              <>
                <Spinner animation="border" size="sm" className="me-2" />
                Speichert...
              </>
            ) : (
              <>
                <Save size={16} className="me-2" />
                Alle {getTotals().modules} Module speichern
              </>
            )}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
}
